/**
 * Move .btn-carousel below carousel navigation
 *
 * Positions .btn-carousel immediately after .splide__arrows within carousel
 * containers to display a CTA button beneath the arrows/dots nav.
 * Runs on initial load, window resize, and Splide lifecycle events.
 *
 * @since 1.0.0
 */
export function initCarouselInsertButton() {
    /**
     * Helper function to move button below arrows navigation
     * @param {Element} carousel - The carousel element to process
     */
    function moveButtonBelowNav(carousel) {
        const arrows = carousel.querySelector('.splide__arrows');
        const buttons = Array.from(carousel.querySelectorAll('.btn-carousel')).filter((button) => {
            /*
             * A page can contain more than one carousel, and a carousel can also
             * contain nested carousel markup while Splide is preparing the DOM.
             * Keep each CTA scoped to the carousel that originally owns it so a
             * global lookup cannot move the first button into every carousel on
             * the page.
             */
            return button.closest('.carousel-split, .carousel-dynamic') === carousel;
        });

        // Only move when this carousel has both navigation arrows and its own CTA.
        if (arrows && buttons.length) {
            let insertionTarget = arrows;

            buttons.forEach((button) => {
                /*
                 * Insert after the last moved item instead of repeatedly inserting
                 * after the arrows container. That preserves editor-authored order
                 * when a carousel has more than one CTA-style button.
                 */
                insertionTarget.after(button);
                insertionTarget = button;
            });
        }
    }

    /**
     * Move button in all carousels
     */
    function moveButtonAllCarousels() {
        const carousels = document.querySelectorAll('.carousel-split, .carousel-dynamic');
        carousels.forEach((carousel) => {
            moveButtonBelowNav(carousel);
        });
    }

    // Initial move on page load
    moveButtonAllCarousels();

    // Move on window resize (handles responsive breakpoint changes)
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(moveButtonAllCarousels, 150);
    });

    // Move when Splide regenerates/updates
    // Listen for Splide events on document since carousels might be added dynamically
    document.addEventListener('splide:mounted', (e) => {
        const carousel = e.target.closest('.carousel-split, .carousel-dynamic');
        if (carousel) {
            moveButtonBelowNav(carousel);
        }
    });

    document.addEventListener('splide:updated', (e) => {
        const carousel = e.target.closest('.carousel-split, .carousel-dynamic');
        if (carousel) {
            moveButtonBelowNav(carousel);
        }
    });

    document.addEventListener('splide:resized', (e) => {
        const carousel = e.target.closest('.carousel-split, .carousel-dynamic');
        if (carousel) {
            moveButtonBelowNav(carousel);
        }
    });
}
